import { WikiPage } from "TFS/Wiki/Contracts";
import { ViewActionsHub, PageDeletedPayload, MovePageCompletedPayload } from "Wiki/Scenarios/Overview/ViewActionsHub";
import { ViewStoresHub } from "Wiki/Scenarios/Overview/Stores/ViewStoresHub";
import { Attachment } from "Wiki/Scenarios/Shared/SharedActionsHub";
import { TelemetryWriter } from "Wiki/Scenarios/Shared/Sources/TelemetryWriter";
import { WikiActionIds } from "Wiki/Scripts/CommonConstants";
import {
    PageErrorType,
    SaveOptionType,
    SavePageOperationType,
    TelemetryConstants,
    WikiTypeCI
} from "Wiki/Scripts/CustomerIntelligenceConstants";
import { getDepthOfPage } from "Wiki/Scripts/Helpers";
import { isInclude } from "Wiki/Scripts/WikiPagesHelper";

/**
 * Writes telemetry events based on the Overview actions invoked in Flux.
 */
export class TelemetrySpy implements IDisposable {
    constructor(
        private _telemetryWriter: TelemetryWriter,
        private _viewActionsHub: ViewActionsHub,
        private _storesHub: ViewStoresHub
    ) {
        this._registerActionsHubHandlers();
        this._registerDialogActionHandlers();
    }

    public dispose(): void {
        this._disposeActionsHubHandlers();
        this._disposeDialogActionHandlers();
    }
    
    public setWikiType(wikiType: WikiTypeCI): void{
        this._telemetryWriter.setWikiType(wikiType);
    }

    public publishPageError(errorMessage: string, errorType: PageErrorType): void {
        this._telemetryWriter.publish(
            TelemetryConstants.PageError,
            {
                errorMessage: errorMessage,
                errorType: PageErrorType[errorType],
            });
    }

    public publishNonConformantPageAction(): void {
        this._telemetryWriter.publish(TelemetryConstants.NonConformancePageAction);
    }

    public publishPageSaved(saveOptionType: SaveOptionType, savePageOperationType: SavePageOperationType, wikiPage: WikiPage, isDefaultCommentChanged: boolean): void {
        let props = {
            saveOptionType: SaveOptionType[saveOptionType],
            savePageOperationType: SavePageOperationType[savePageOperationType],
            depth: getDepthOfPage(wikiPage.path),
            order: wikiPage.order,
            isDefaultCommentChanged: isDefaultCommentChanged,
        };

        props[TelemetryConstants.IsIncludePageKey] = isInclude(wikiPage.path);
        this._telemetryWriter.publish(TelemetryConstants.PageSaved, props);
    }

    public publishSearchWithWikiSearchClicked(): void {
        this._telemetryWriter.publish(TelemetryConstants.PublishSearchWithWikiSearchClicked);
    }

    public publishConflictError(wikiPage: WikiPage): void {
        this._telemetryWriter.publish(
            TelemetryConstants.ConflictErrorOnPageSave,
            {
                depth: getDepthOfPage(wikiPage.path),
                order: wikiPage.order,
            });
    }

    public publishSecurityDialogPrompted(): void {
        this._telemetryWriter.publish(TelemetryConstants.SecurityDialogPrompted);
    }

    public publishCopyCloneUrlClicked(): void {
        this._telemetryWriter.publish(TelemetryConstants.CopyCloneUrlClicked);
    }

    public publishPagePrinted(): void {
        this._telemetryWriter.publish(TelemetryConstants.PagePrinted);
    }

    public publishEditContainerPreviewToggled(previewMode: string): void {
        this._telemetryWriter.publish(
            TelemetryConstants.EditContainerPreviewToggled,
            {
                preview: previewMode,
            }
        );
    }

    public publishPageFilterPerformed(): void {
        this._telemetryWriter.publish(TelemetryConstants.PageFilterPerformed);
    }

    public publishPageEditingCancelledWithoutAnyChange(): void {
        this._telemetryWriter.publish(TelemetryConstants.PageEditingCancelledWithoutAnyChange);
    }

    public publishFilteredPagesClickedCount(
        filteredPagesClickCount: number,
        queryLength: number,
        isFilteredResult: boolean,
        isFuzzyResult: boolean): void {

        this._telemetryWriter.publish(
            TelemetryConstants.FilteredPageClicked,
            {
                queryLength,
                isFilteredResult,
                filteredPagesClickCount,
                isFuzzyResult,
            });
    }

    public publishBrokenLinkErrorPageVisited(): void {
        this._telemetryWriter.publish(TelemetryConstants.BrokenLinkErrorPageVisited);
    }

    public publishBrokenLinkWithBrokenParentVisited(): void {
        this._telemetryWriter.publish(TelemetryConstants.BrokenLinkWithBrokenParentVisited);
    }

    public publishPageCreatedAtBrokenLink(): void {
        this._telemetryWriter.publish(TelemetryConstants.PageCreatedAtBrokenLink);
    }

    public publishAddPageContent(): void {
        this._telemetryWriter.publish(TelemetryConstants.AddPageContentToParentPagesWithoutMDFiles);
    }

    public publishPageCreatedWithTemplate(): void {
        this._telemetryWriter.publish(TelemetryConstants.PageCreatedWithTemplate);
    }

    public publishSplitterToggleButtonClicked(expanded: boolean, mode: string): void {
        this._telemetryWriter.publish(TelemetryConstants.WikiSplitterToggleButtonClicked, {mode, expanded});
    }

    public publishHeaderMenuItemClicked(featureName: string): void {
        this._telemetryWriter.publish(featureName, {source: TelemetryConstants.HeaderMenu});
    }

    public publishTreeNodeMenuItemClicked(featureName: string): void {
        const mode = this._storesHub.state.sharedState.urlState.action === WikiActionIds.Edit
         ? TelemetryConstants.EditMode : TelemetryConstants.ViewMode;
        this._telemetryWriter.publish(featureName, {source: TelemetryConstants.TreeNodeMenu, mode});
    }

    public publishWitQueryAttach(pageId: number | undefined, queryId: string, projectId: string, wikiId: string): void {
        this._telemetryWriter.publish(
            TelemetryConstants.WitQueryAttached,
            {
                pageId,
                queryId,
                projectId,
                wikiId
            });
    }

    public publishWikiPageViewedWithFeatures(pageId: number | undefined, wikiId: string, extraProperties: IDictionaryStringTo<boolean> = {}): void {
        this._telemetryWriter.publish(
            TelemetryConstants.PageViewed,
            {
                pageId,
                wikiId,
                ...extraProperties
            });
    }

    private _registerActionsHubHandlers(): void {
        this._viewActionsHub.attachmentsAdded.addListener(this._publishAttachmentAdded);
        this._viewActionsHub.cancelPageEditing.addListener(this._publishPageEditingAbandoned);
        this._viewActionsHub.pageDeleted.addListener(this._publishPageDeleted);
        this._viewActionsHub.movePageCompleted.addListener(this._publishPageMoved);
        this._viewActionsHub.setAsHomePageCompleted.addListener(this._publishPageSetAsHomePage);
        this._viewActionsHub.wikiRenameSucceeded.addListener(this._renameWikiSucceeded);
        this._viewActionsHub.wikiRenameFailed.addListener(this._renameWikiFailed);
    }

    private _disposeActionsHubHandlers(): void {
        this._viewActionsHub.attachmentsAdded.removeListener(this._publishAttachmentAdded);
        this._viewActionsHub.cancelPageEditing.removeListener(this._publishPageEditingAbandoned);
        this._viewActionsHub.pageDeleted.removeListener(this._publishPageDeleted);
        this._viewActionsHub.movePageCompleted.removeListener(this._publishPageMoved);
        this._viewActionsHub.setAsHomePageCompleted.removeListener(this._publishPageSetAsHomePage);
    }

    private _registerDialogActionHandlers(): void {
        this._viewActionsHub.deletePageDialogPrompted.addListener(this._publishDeletePageDialogPrompted);
        this._viewActionsHub.deletePageDialogDismissed.addListener(this._publishDeletePageDialogDismissed);
        this._viewActionsHub.movePageDialogPrompted.addListener(this._publishMovePageDialogPrompted);
        this._viewActionsHub.movePageDialogDismissed.addListener(this._publishMovePageDialogDismissed);
        this._viewActionsHub.setAsHomePageDialogPrompted.addListener(this._publishSetAsHomePageDialogPrompted);
        this._viewActionsHub.setAsHomePageDialogDismissed.addListener(this._publishSetAsHomePageDialogDismissed);
        this._viewActionsHub.movePagePickerDialogPrompted.addListener(this._publishMovePagePickerDialogPrompted);
    }

    private _disposeDialogActionHandlers(): void {
        this._viewActionsHub.deletePageDialogPrompted.removeListener(this._publishDeletePageDialogPrompted);
        this._viewActionsHub.deletePageDialogDismissed.removeListener(this._publishDeletePageDialogDismissed);
        this._viewActionsHub.movePageDialogPrompted.removeListener(this._publishMovePageDialogPrompted);
        this._viewActionsHub.movePageDialogDismissed.removeListener(this._publishMovePageDialogDismissed);
        this._viewActionsHub.setAsHomePageDialogPrompted.removeListener(this._publishSetAsHomePageDialogPrompted);
        this._viewActionsHub.setAsHomePageDialogDismissed.removeListener(this._publishSetAsHomePageDialogDismissed);
        this._viewActionsHub.movePagePickerDialogPrompted.removeListener(this._publishMovePagePickerDialogPrompted);
    }

    private _publishAttachmentAdded = (payload: Attachment[]): void => {
        this._telemetryWriter.publish(TelemetryConstants.AttachmentsAdded);
    }

    private _publishPageEditingAbandoned = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.PageEditingAbandoned);
    }

    private _publishPageDeleted = (pageDeletedPayload: PageDeletedPayload): void => {
        let props = {};
        props[TelemetryConstants.IsIncludePageKey] = isInclude(pageDeletedPayload.pagePath);
        this._telemetryWriter.publish(TelemetryConstants.PageDeleted, props);
    }

    private _publishPageMoved = (pageMovedPayload: MovePageCompletedPayload): void => {
        this._telemetryWriter.publish(TelemetryConstants.PageMoved, {
            [TelemetryConstants.IsSourcePageIncludeKey]: isInclude(pageMovedPayload.sourcePath),
            [TelemetryConstants.IsDestinationPageIncludeKey]: isInclude(pageMovedPayload.newPath),
        });
    }

    private _publishPageSetAsHomePage = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.PageSetAsHomePage);
    }

    private _publishDeletePageDialogPrompted = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.DeletePageDialogPrompted);
    }

    private _publishDeletePageDialogDismissed = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.DeletePageDialogDismissed);
    }

    private _publishMovePageDialogPrompted = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.MovePageDialogPrompted);
    }

    private _publishMovePageDialogDismissed = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.MovePageDialogDismissed);
    }

    private _publishSetAsHomePageDialogPrompted = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.SetAsHomePageDialogPrompted);
    }

    private _publishSetAsHomePageDialogDismissed = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.SetAsHomePageDialogDismissed);
    }

    private _publishMovePagePickerDialogPrompted = (): void => {
        this.publishTreeNodeMenuItemClicked(TelemetryConstants.MovePagePickerDialogPrompted);
    }

    private _renameWikiSucceeded = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.WikiRenameSucceeded);
    }

    private _renameWikiFailed = (): void => {
        this._telemetryWriter.publish(TelemetryConstants.WikiRenameFailed);
    }

}
